import { useEffect, useState } from 'react'
import { msg, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { SUPPORTED_LOCALES } from '../../consts/supported-locales'

const LOCALE_NAMES = {
  en: 'English',
  fr: 'Français',
  es: 'Español',
  ru: 'Русский',
  tg: 'Тоҷикӣ',
}

const TAB_FIELD = 'field'
const TAB_OPTIONS = 'options'

function buildFieldDraft(canonicalLabel, labels = {}) {
  const draft = {}
  SUPPORTED_LOCALES.forEach((code) => {
    draft[code] = labels?.[code] ?? (code === 'en' ? canonicalLabel : '')
  })
  return draft
}

function buildOptionsDraft(options = []) {
  return options.map((option) => {
    const labels = {}
    SUPPORTED_LOCALES.forEach((code) => {
      labels[code] = option.labels?.[code] ?? (code === 'en' ? option.label ?? '' : '')
    })
    return { ...option, labels }
  })
}

function cleanLabels(labels = {}) {
  const cleaned = {}
  SUPPORTED_LOCALES.forEach((code) => {
    const value = String(labels[code] ?? '').trim()
    if (value) cleaned[code] = value
  })
  return cleaned
}

/**
 * Dialog unificato per traduzioni campo config e opzioni select (admin).
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} props.canonicalLabel
 * @param {string} [props.fieldType]
 * @param {Record<string, string>} [props.labels]
 * @param {Array<{ value: string | number, label: string, labels?: Record<string, string>, flag?: string }>} [props.options]
 * @param {(payload: { labels: Record<string, string>, options?: Array<object> }) => void} props.onSave
 */
export default function ConfigLabelsDialog({
  open,
  onClose,
  canonicalLabel,
  fieldType,
  labels = {},
  options = [],
  onSave,
}) {
  const theme = useTheme()
  const { _ } = useLingui()
  const [activeTab, setActiveTab] = useState(TAB_FIELD)
  const [fieldDraft, setFieldDraft] = useState({})
  const [optionsDraft, setOptionsDraft] = useState([])
  const titleColor = theme.palette.newColors?.gray || theme.palette.text.primary
  const isSelect = fieldType === 'select'
  const canSave = Boolean(String(canonicalLabel ?? '').trim())

  useEffect(() => {
    if (!open) return
    setActiveTab(TAB_FIELD)
    setFieldDraft(buildFieldDraft(canonicalLabel, labels))
    setOptionsDraft(buildOptionsDraft(options))
  }, [open, labels, canonicalLabel, options])

  const handleOptionChange = (optionIndex, locale, value) => {
    setOptionsDraft((prev) =>
      prev.map((option, index) =>
        index === optionIndex
          ? { ...option, labels: { ...option.labels, [locale]: value } }
          : option
      )
    )
  }

  const handleSave = () => {
    const cleanedFieldLabels = cleanLabels(fieldDraft)
    const payload = { labels: cleanedFieldLabels }
    if (isSelect) {
      payload.options = optionsDraft.map((option) => ({
        ...option,
        labels: cleanLabels(option.labels),
      }))
    }
    onSave(payload)
    onClose()
  }

  const canonicalBox = (
    <Box
      sx={{
        mb: 2.5,
        px: 1.5,
        py: 1,
        borderRadius: 1,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor:
          theme.palette.mode === 'light'
            ? theme.palette.grey[50]
            : theme.palette.action.hover,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: theme.palette.text.secondary,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          mb: 0.5,
        }}
      >
        <Trans>Canonical label (data key)</Trans>
      </Typography>
      <Typography
        component="code"
        sx={{
          fontFamily: 'var(--font-mono, monospace)',
          fontSize: '0.9rem',
          fontWeight: 600,
          color: titleColor,
          wordBreak: 'break-word',
        }}
      >
        {canonicalLabel || '—'}
      </Typography>
    </Box>
  )

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isSelect && activeTab === TAB_OPTIONS ? 'md' : 'sm'}
      fullWidth
    >
      <DialogTitle
        sx={{
          color: titleColor,
          fontWeight: 700,
          pr: 6,
        }}
      >
        <Trans>Translations</Trans>
        <IconButton
          aria-label={_(msg`Close`)}
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            color: titleColor,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      {isSelect && (
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value={TAB_FIELD} label={_(msg`Field label`)} />
          <Tab
            value={TAB_OPTIONS}
            label={_(msg`Options`)}
            disabled={!options?.length}
          />
        </Tabs>
      )}
      <DialogContent sx={{ pt: 2 }}>
        {(!isSelect || activeTab === TAB_FIELD) && (
          <>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
              <Trans>
                Edit display labels per language. The canonical key below is used for data storage and must not change when you only update translations.
              </Trans>
            </Typography>
            {canonicalBox}
            <Stack spacing={2}>
              {SUPPORTED_LOCALES.map((code) => (
                <TextField
                  key={code}
                  label={`${LOCALE_NAMES[code] ?? code} (${code.toUpperCase()})`}
                  value={fieldDraft[code] ?? ''}
                  onChange={(e) =>
                    setFieldDraft((prev) => ({ ...prev, [code]: e.target.value }))
                  }
                  fullWidth
                  size="small"
                  placeholder={code === 'en' ? canonicalLabel : ''}
                />
              ))}
            </Stack>
          </>
        )}
        {isSelect && activeTab === TAB_OPTIONS && (
          <>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
              <Trans>
                Edit option display labels per language. Option values and canonical labels are preserved for stored data.
              </Trans>
            </Typography>
            {canonicalBox}
            <Stack spacing={2.5}>
              {optionsDraft.map((option, optionIndex) => (
                <Box
                  key={`${option.value}-${option.label}-${optionIndex}`}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    p: 1.5,
                  }}
                >
                  <TextField
                    label={_(msg`Canonical label`)}
                    value={option.label ?? ''}
                    size="small"
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={{ mb: 1.5 }}
                  />
                  <Stack spacing={1.5}>
                    {SUPPORTED_LOCALES.map((code) => (
                      <TextField
                        key={`${option.value}-${code}`}
                        label={`${LOCALE_NAMES[code] ?? code} (${code.toUpperCase()})`}
                        value={option.labels?.[code] ?? ''}
                        onChange={(e) => handleOptionChange(optionIndex, code, e.target.value)}
                        fullWidth
                        size="small"
                        placeholder={code === 'en' ? option.label ?? '' : ''}
                      />
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
            {optionsDraft.length === 0 && (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                <Trans>No options available for this select field.</Trans>
              </Typography>
            )}
          </>
        )}
        {!canSave && (
          <Typography variant="caption" sx={{ display: 'block', mt: 2, color: theme.palette.warning.main }}>
            <Trans>Set the canonical label before saving translations.</Trans>
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <Button
          onClick={onClose}
          sx={{ color: theme.palette.newColors?.gray || theme.palette.text.secondary }}
        >
          <Trans>Cancel</Trans>
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
          sx={{
            background: theme.palette.newColors?.cyanGradient,
            color: theme.palette.newColors?.lightGray ?? 'white',
            fontWeight: 600,
            '&:hover': {
              background: theme.palette.newColors?.cyanGradient,
              opacity: 0.9,
            },
            '&.Mui-disabled': {
              opacity: 0.5,
            },
          }}
        >
          <Trans>Save</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  )
}
